/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { assignmentApi } from '../services/assignmentApi';
import AssignedTerminal from '../components/assignments/AssignedTerminal';
import PodLogStream from '../components/assignments/PodLogStream';

type Tab = 'overview' | 'logs' | 'terminal' | 'actions';

const AssignedPodDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');

  const detail = useQuery({
    queryKey: ['assigned-pod', id],
    queryFn: () => assignmentApi.getPod(id),
    refetchInterval: tab === 'overview' ? 10000 : false,
    enabled: !!id,
  });

  const restart = useMutation({
    mutationFn: () => assignmentApi.restartPod(id),
    onSuccess: (res) => {
      if (res.has_controller) {
        toast.success(`Pod restart triggered. Controller (${res.controller_kind}) will recreate it.`);
      } else {
        toast.success('Pod deleted. No controller - pod will not auto-recreate.');
      }
      qc.invalidateQueries({ queryKey: ['assigned-pod', id] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error || err?.message || 'Restart failed'),
  });

  const live = detail.data?.live;
  const row = detail.data?.data;
  const isActive = row?.status === 'active';
  const isMissing = live?.phase === 'missing' || live?.phase === 'unknown';

  if (detail.isLoading) {
    return (
      <div className="p-6">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (detail.isError || !row) {
    return (
      <div className="p-6">
        <div className="text-red-400">Failed to load pod assignment.</div>
        <Link to="/assignments" className="text-primary-500 underline mt-2 block">
          Back to Assigned Resources
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/assignments"
        className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Assigned Resources
      </Link>

      <h1 className="text-2xl font-bold text-white mb-2">{row.display_name}</h1>
      <p className="text-sm text-gray-400 mb-4">
        {row.namespace} / {row.pod_name}
        {row.container_name ? ` (container: ${row.container_name})` : ''}
      </p>

      {/* Banners */}
      {isMissing && (
        <div className="mb-4 bg-red-900/30 border border-red-700/50 text-red-400 p-3 rounded-lg text-sm">
          Pod no longer exists in the cluster - contact your administrator.
        </div>
      )}
      {row.status === 'suspended' && (
        <div className="mb-4 bg-yellow-900/30 border border-yellow-700/50 text-yellow-400 p-3 rounded-lg text-sm">
          This pod is suspended due to low wallet balance. Actions are disabled until reactivated.{' '}
          <Link to="/billing/payments?action=topup" className="underline">
            Top up wallet
          </Link>
          .
        </div>
      )}
      {row.status === 'removed' && (
        <div className="mb-4 bg-gray-800 border border-gray-700 text-gray-400 p-3 rounded-lg text-sm">
          This assignment has been removed by an administrator.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-700 mb-6">
        {(['overview', 'logs', 'terminal', 'actions'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`pb-2 capitalize text-sm font-medium transition-colors ${
              tab === t
                ? 'border-b-2 border-primary-500 text-primary-500'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="space-y-3 text-sm">
          <div className="flex gap-2">
            <span className="text-gray-400 w-32">Phase:</span>
            <span className="text-white">{live?.phase || '-'}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-400 w-32">Ready:</span>
            <span className="text-white">{live?.ready ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-400 w-32">Restart count:</span>
            <span className="text-white">{live?.restart_count ?? '-'}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-400 w-32">Node:</span>
            <span className="text-white">{live?.node_name || '-'}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-400 w-32">Started at:</span>
            <span className="text-white">{live?.started_at || '-'}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-400 w-32">Monthly cost:</span>
            <span className="text-white">₹{row.monthly_cost.toFixed(2)}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-400 w-32">Status:</span>
            <span className="text-white capitalize">{row.status}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-400 w-32">Assigned at:</span>
            <span className="text-white">{new Date(row.assigned_at).toLocaleString()}</span>
          </div>
          {row.notes && (
            <div className="flex gap-2">
              <span className="text-gray-400 w-32">Notes:</span>
              <span className="text-white">{row.notes}</span>
            </div>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {tab === 'logs' && (
        <div>
          {!isActive ? (
            <div className="text-gray-400 text-sm">
              Logs unavailable - pod is {row.status}.
            </div>
          ) : (
            <PodLogStream wsUrl={assignmentApi.podLogsWsURL(id)} />
          )}
        </div>
      )}

      {/* Terminal Tab */}
      {tab === 'terminal' && (
        <div>
          {isActive && !isMissing ? (
            <AssignedTerminal
              wsUrl={assignmentApi.podTerminalWsURL(id)}
              title={row.display_name}
            />
          ) : (
            <div className="text-gray-400 text-sm">
              Terminal unavailable - pod is {isMissing ? 'missing from cluster' : row.status}.
            </div>
          )}
        </div>
      )}

      {/* Actions Tab */}
      {tab === 'actions' && (
        <div className="space-y-4">
          <div className="border border-gray-700 rounded-lg p-4">
            <h3 className="font-medium text-white mb-1">Restart Pod</h3>
            <p className="text-sm text-gray-400 mb-3">
              Deletes the pod. If a controller (Deployment, StatefulSet, etc.) manages it, the pod
              will be automatically recreated. Any in-pod state will be lost.
            </p>
            <button
              disabled={!isActive || isMissing || restart.isPending}
              onClick={() => {
                if (window.confirm('Restart this pod? Any in-pod state will be lost.')) {
                  restart.mutate();
                }
              }}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {restart.isPending ? 'Restarting...' : 'Restart Pod'}
            </button>
            {(!isActive || isMissing) && (
              <p className="text-xs text-gray-500 mt-2">
                {isMissing
                  ? 'Pod is missing from the cluster.'
                  : `Pod is ${row.status} - actions disabled.`}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignedPodDetail;
